import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Home, Printer, Volume2, VolumeX, Bell, Check, RefreshCw, MapPin, Phone, Clock, LayoutDashboard, FileText, Ban, Trash2 } from 'lucide-react';
import { buildReceiptHTML, buildCloseDayReceiptHTML } from '../utils/receiptTemplate';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALERT_SOUND = '/sounds/order-alert.wav';

const RestaurantPOSDashboard = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(
    () => localStorage.getItem('pos_restaurant_id') || ''
  );
  const [orders, setOrders] = useState([]);
  const [autoPrint, setAutoPrint] = useState(
    () => localStorage.getItem('pos_auto_print') !== 'false'
  );
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem('pos_sound') !== 'false'
  );
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [alarming, setAlarming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteExclude, setDeleteExclude] = useState(false);

  const audioRef = useRef(null);
  const printFrameRef = useRef(null);
  const wsRef = useRef(null);
  const handledOrderIdsRef = useRef(new Set());

  // Persist settings
  useEffect(() => { localStorage.setItem('pos_auto_print', String(autoPrint)); }, [autoPrint]);
  useEffect(() => { localStorage.setItem('pos_sound', String(soundEnabled)); }, [soundEnabled]);
  useEffect(() => {
    if (selectedRestaurantId) localStorage.setItem('pos_restaurant_id', selectedRestaurantId);
  }, [selectedRestaurantId]);

  // Load restaurants list (for selector). Restaurant managers only see their own restaurants.
  useEffect(() => {
    fetch(`${API_URL}/api/restaurants`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        let visible = data;
        if (user && user.role === 'restaurant_manager') {
          const managed = (user.restaurant_ids && user.restaurant_ids.length > 0)
            ? user.restaurant_ids
            : (user.restaurant_id ? [user.restaurant_id] : []);
          visible = data.filter(r => managed.includes(r.restaurant_id));
        }
        setRestaurants(visible);
        // Auto-select if nothing chosen yet, or if previous selection is no longer visible
        if (visible.length > 0) {
          const stillVisible = selectedRestaurantId === '__ALL__' || visible.some(r => r.restaurant_id === selectedRestaurantId);
          if (!selectedRestaurantId || !stillVisible) {
            // Admin defaults to ALL so they receive every order on the same device
            const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
            setSelectedRestaurantId(isAdmin ? '__ALL__' : visible[0].restaurant_id);
          }
        }
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  const isAllMode = selectedRestaurantId === '__ALL__';
  const isAdminUser = user && (user.role === 'admin' || user.role === 'super_admin');
  const selectedRestaurant = restaurants.find(r => r.restaurant_id === selectedRestaurantId);

  // Persist selection
  useEffect(() => {
    if (selectedRestaurantId) localStorage.setItem('pos_restaurant_id', selectedRestaurantId);
  }, [selectedRestaurantId]);

  // Helper: lookup a restaurant for an order (used in ALL mode)
  const orderRestaurantName = (order) => {
    const rid = order.restaurant_id || order.items?.[0]?.restaurant_id;
    const r = restaurants.find(x => x.restaurant_id === rid);
    return r?.name || rid || '?';
  };

  // Fetch existing orders for the restaurant (or every restaurant if ALL)
  const fetchOrders = useCallback(async () => {
    if (!selectedRestaurantId || !token) return;
    try {
      const url = isAllMode
        ? `${API_URL}/api/admin/pos/all-orders`
        : `${API_URL}/api/restaurant/${selectedRestaurantId}/orders`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
        data.forEach(o => handledOrderIdsRef.current.add(o.order_id));
      }
    } catch (e) {
      console.error('Failed to fetch orders:', e);
    }
  }, [selectedRestaurantId, token, isAllMode]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Self-heal poll: re-sync the board every 15s so a missed WebSocket event
  // (e.g. a cancellation) never leaves a stale order on screen until manual refresh.
  useEffect(() => {
    if (!selectedRestaurantId || !token) return;
    const id = setInterval(() => { fetchOrders(); }, 15000);
    return () => clearInterval(id);
  }, [fetchOrders, selectedRestaurantId, token]);

  // Close-day handler — only admin sees the button. Calls the backend to
  // archive all delivered+cancelled orders of today, prints a per-restaurant
  // closing summary, then refetches so the panel is clean.
  const [closingDay, setClosingDay] = useState(false);
  const closeDay = useCallback(async (activeCount = 0) => {
    const msg = activeCount > 0
      ? (language === 'pt'
        ? `⚠️ Existem ${activeCount} pedido(s) ABERTO(S) (ainda não entregues nem cancelados). Estes NÃO serão fechados — feche-os primeiro (Entregue/Cancelar).\n\nQuer mesmo fechar o dia assim?`
        : `⚠️ There are ${activeCount} OPEN order(s) (not delivered or cancelled). These will NOT be closed — finish them first.\n\nClose the day anyway?`)
      : (language === 'pt'
        ? 'Fechar o dia? Vai imprimir o resumo de TODOS os restaurantes e limpar do painel todos os pedidos terminados (entregues/cancelados).'
        : 'Close the day? This prints a summary of ALL restaurants and clears every finished order from the panel.');
    if (!window.confirm(msg)) return;
    setClosingDay(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pos/close-day`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        // Render the closing summary into the same hidden iframe used for orders.
        const html = buildCloseDayReceiptHTML(data);
        const iframe = printFrameRef.current;
        if (iframe) {
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          doc.open(); doc.write(html); doc.close();
          setTimeout(() => {
            try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error(e); }
          }, 350);
        }
        toast.success(language === 'pt'
          ? `Fechado: ${data.archived_count} pedidos arquivados, total faturado: €${(data.grand_total?.total || 0).toFixed(2)}`
          : `Closed: ${data.archived_count} orders archived, total: €${(data.grand_total?.total || 0).toFixed(2)}`,
          { duration: 6000 });
        await fetchOrders();
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Erro');
      }
    } catch (e) {
      console.error('close day error:', e);
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    } finally {
      setClosingDay(false);
    }
  }, [token, language, fetchOrders]);

  const printOrder = useCallback((order) => {
    try {
      // In ALL mode, look up the order's actual restaurant so the receipt
      // header shows the right name (kitchen/customer name).
      const restForPrint = isAllMode
        ? restaurants.find(r => r.restaurant_id === (order.restaurant_id || order.items?.[0]?.restaurant_id)) || selectedRestaurant
        : selectedRestaurant;
      const html = buildReceiptHTML(order, restForPrint);
      const iframe = printFrameRef.current;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      // Give the iframe a tick to render
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print error:', e);
        }
      }, 250);
    } catch (e) {
      console.error('Build receipt error:', e);
    }
  }, [selectedRestaurant, isAllMode, restaurants]);

  // Stop alarm
  const stopAlarm = useCallback(() => {
    // Native bridge first (if running inside Android app)
    if (typeof window !== 'undefined' && window.WegooPOS && window.WegooPOS.stopAlarm) {
      try { window.WegooPOS.stopAlarm(); } catch (e) { console.debug('native stopAlarm bridge missing', e); }
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAlarming(false);
  }, []);

  // Start alarm (loop)
  const startAlarm = useCallback(() => {
    if (!soundEnabled) return;
    // Native bridge first (Android app — louder, ignores autoplay restrictions)
    if (typeof window !== 'undefined' && window.WegooPOS && window.WegooPOS.playAlarm) {
      try {
        window.WegooPOS.setMaxVolume?.();
        window.WegooPOS.playAlarm();
        setAlarming(true);
        return;
      } catch (e) { console.warn('Native alarm bridge failed', e); }
    }
    if (!audioRef.current) return;
    audioRef.current.loop = true;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(err => {
      console.warn('Audio play blocked. Click anywhere to unlock.', err);
    });
    setAlarming(true);
  }, [soundEnabled]);

  // Unlock audio with first user interaction
  useEffect(() => {
    const unlock = () => {
      if (audioRef.current && !audioUnlocked) {
        // Play+pause silently to satisfy autoplay policy
        audioRef.current.volume = 0.01;
        audioRef.current.play().then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.volume = 1.0;
          setAudioUnlocked(true);
        }).catch(() => {});
      }
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [audioUnlocked]);

  // Explicit one-tap activation (start of shift) to satisfy browser autoplay
  // policy. Without a user gesture the alarm sound is blocked even though the
  // visual alarm state triggers — this button guarantees sound works.
  const armPos = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.01;
      audioRef.current.play().then(() => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        setAudioUnlocked(true);
      }).catch(() => setAudioUnlocked(true));
    } else {
      setAudioUnlocked(true);
    }
  }, []);

  // WebSocket: subscribe to restaurant channel(s).
  // In ALL mode (admin) we open one socket per restaurant so we don't miss any.
  useEffect(() => {
    if (!selectedRestaurantId) return;
    const wsUrlBase = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

    // Channels to listen on. Food orders are broadcast to restaurant_{rid};
    // ice/shisha (platform-level) are broadcast to the "admin" channel, so in
    // ALL mode (admin) we also subscribe to "admin" to auto-print them.
    const channels = isAllMode
      ? [...restaurants.map(r => `restaurant_${r.restaurant_id}`), 'admin']
      : [`restaurant_${selectedRestaurantId}`];
    if (channels.length === 0) return;

    const handleMessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_order' && msg.order) {
          const o = msg.order;
          if (handledOrderIdsRef.current.has(o.order_id)) return;
          handledOrderIdsRef.current.add(o.order_id);

          setOrders(prev => [o, ...prev.filter(x => x.order_id !== o.order_id)]);
          const rname = orderRestaurantName(o);
          toast.success(language === 'pt'
            ? `Novo pedido ${isAllMode ? `para ${rname} ` : ''}#${o.order_id?.slice(-6)}!`
            : `New order ${isAllMode ? `for ${rname} ` : ''}#${o.order_id?.slice(-6)}!`);
          startAlarm();
          if (autoPrint) {
            printOrder(o);
          }
        } else if (msg.type === 'order_update' && msg.order) {
          setOrders(prev => prev.map(o => o.order_id === msg.order.order_id ? msg.order : o));
        } else if (msg.type === 'order_cancelled' && msg.order_id) {
          setOrders(prev => prev.map(o => o.order_id === msg.order_id ? { ...o, status: 'cancelled' } : o));
          toast.error(language === 'pt'
            ? `⚠️ Pedido #${msg.order_id?.slice(-6)} CANCELADO pelo cliente!`
            : `⚠️ Order #${msg.order_id?.slice(-6)} CANCELLED by customer!`,
            { duration: 12000 });
          startAlarm();
        } else if (msg.type === 'order_deleted' && msg.order_id) {
          setOrders(prev => prev.filter(o => o.order_id !== msg.order_id));
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    const sockets = channels.map(ch => {
      const ws = new WebSocket(`${wsUrlBase}/api/ws/${ch}`);
      ws.onmessage = handleMessage;
      ws.onerror = (e) => console.warn('WS error:', e);
      return ws;
    });
    wsRef.current = sockets[0];

    return () => {
      sockets.forEach(ws => { try { ws.close(); } catch (e) { console.debug('ws close failed', e); } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantId, autoPrint, startAlarm, printOrder, language, isAllMode, restaurants]);

  const updateStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, ...data.order, status: data.order?.status || status } : o));
        if (status === 'cancelled') {
          const refund = data.order?.refund_amount || 0;
          toast.success(
            language === 'pt'
              ? (refund > 0 ? `Pedido cancelado. Reembolso de €${refund.toFixed(2)} emitido.` : 'Pedido cancelado')
              : (refund > 0 ? `Order cancelled. €${refund.toFixed(2)} refunded.` : 'Order cancelled')
          );
        } else {
          toast.success(language === 'pt' ? 'Estado atualizado' : 'Status updated');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? `Erro a atualizar (${res.status})` : `Update failed (${res.status})`));
      }
    } catch (e) {
      toast.error(language === 'pt' ? `Erro de rede: ${e?.message || ''}` : `Network error: ${e?.message || ''}`);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const orderId = deleteTarget.order_id;
    try {
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}?exclude_from_reports=${deleteExclude}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setOrders(prev => prev.filter(o => o.order_id !== orderId));
        toast.success(language === 'pt'
          ? (deleteExclude ? 'Pedido eliminado e removido dos relatórios' : 'Pedido eliminado (continua nos relatórios)')
          : 'Order deleted');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? 'Erro ao eliminar' : 'Delete failed'));
      }
    } catch (e) {
      toast.error(language === 'pt' ? `Erro de rede: ${e?.message || ''}` : `Network error: ${e?.message || ''}`);
    } finally {
      setDeleteTarget(null);
      setDeleteExclude(false);
    }
  };

  const statusColor = (s) => ({
    received: 'bg-yellow-100 text-yellow-800',
    preparing: 'bg-blue-100 text-blue-800',
    ready: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  })[s] || 'bg-gray-100 text-gray-800';

  const statusLabel = (s) => language === 'pt' ? ({
    received: 'Recebido', preparing: 'Preparando', ready: 'Pronto',
    out_for_delivery: 'Em Entrega', delivered: 'Entregue', cancelled: 'Cancelado'
  })[s] || s : s;

  // Filter only active orders (not delivered/cancelled) at top
  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const cancelledOrders = orders.filter(o => o.status === 'cancelled');
  const closedOrders = orders.filter(o => o.status === 'delivered');

  if (user && !['admin', 'super_admin', 'restaurant_manager'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <Card className="max-w-md rounded-2xl">
          <CardContent className="p-6 text-center">
            <p className="text-[#004E64] font-semibold mb-2">
              {language === 'pt' ? 'Acesso restrito' : 'Restricted access'}
            </p>
            <p className="text-sm text-[#9FA2B4]">
              {language === 'pt' ? 'Apenas admins e gestores de restaurante podem aceder ao POS.' : 'Only admins and restaurant managers can access POS.'}
            </p>
            <Link to="/"><Button className="mt-4 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">{language === 'pt' ? 'Voltar' : 'Back'}</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hidden audio element */}
      <audio ref={audioRef} src={ALERT_SOUND} preload="auto" />
      {/* Delete order confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteExclude(false); } }}>
        <AlertDialogContent data-testid="pos-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 size={18} className="text-red-600" />
              {language === 'pt' ? 'Eliminar pedido do sistema?' : 'Delete order from the system?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'pt'
                ? `O pedido #${deleteTarget?.order_id?.slice(-6)} deixa de aparecer no POS.`
                : `Order #${deleteTarget?.order_id?.slice(-6)} will be removed from the POS board.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-3 rounded-xl border border-[#004E64]/10 p-3 my-2">
            <Switch checked={deleteExclude} onCheckedChange={setDeleteExclude} data-testid="pos-delete-exclude-switch" />
            <div>
              <p className="text-sm font-semibold text-[#004E64]">
                {language === 'pt' ? 'Não contar no total da plataforma' : "Don't count in platform total"}
              </p>
              <p className="text-xs text-[#9FA2B4]">
                {language === 'pt'
                  ? (deleteExclude ? 'Não aparece nos relatórios financeiros/mensal.' : 'Por defeito, continua a contar nos relatórios.')
                  : (deleteExclude ? 'Excluded from financial/monthly reports.' : 'By default, still counts in reports.')}
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pos-delete-cancel">{language === 'pt' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="pos-delete-confirm">
              {language === 'pt' ? 'Eliminar' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Hidden iframe for printing */}
      <iframe ref={printFrameRef} title="receipt-print" style={{ position: 'absolute', width: 0, height: 0, border: 0, visibility: 'hidden' }} />

      {/* Header */}
      <div className="bg-[#004E64] text-white py-4 px-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/"><Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full" data-testid="pos-home-btn"><Home size={20} /></Button></Link>
            <div>
              <h1 className="text-xl font-bold">
                POS — {isAllMode
                  ? (language === 'pt' ? 'TODOS os Restaurantes' : 'ALL Restaurants')
                  : (selectedRestaurant?.name || (language === 'pt' ? 'Restaurante' : 'Restaurant'))}
              </h1>
              <p className="text-xs opacity-80">{language === 'pt' ? 'Painel de Pedidos em Tempo Real' : 'Real-time Orders Panel'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/restaurant-portal" data-testid="pos-financial-portal-link">
              <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10 rounded-full">
                <LayoutDashboard size={14} className="mr-1" />
                {language === 'pt' ? 'Portal Financeiro' : 'Financial Portal'}
              </Button>
            </Link>
            {(isAdminUser || (user && user.role === 'restaurant_manager')) && (
              <Button
                onClick={() => closeDay(activeOrders.length)}
                disabled={closingDay}
                size="sm"
                className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full"
                data-testid="close-day-btn"
              >
                <FileText size={14} className="mr-1" />
                {closingDay
                  ? (language === 'pt' ? 'A fechar...' : 'Closing...')
                  : (language === 'pt' ? 'Fechar Dia' : 'Close Day')}
              </Button>
            )}
            {alarming && (
              <Button
                onClick={stopAlarm}
                className="bg-red-500 hover:bg-red-600 rounded-full animate-pulse"
                data-testid="stop-alarm-btn"
              >
                <Bell size={16} className="mr-1" /> {language === 'pt' ? 'Parar alarme' : 'Stop alarm'}
              </Button>
            )}
            <Button onClick={fetchOrders} variant="outline" size="icon" className="text-white border-white/30 hover:bg-white/10 rounded-full" data-testid="refresh-orders-btn">
              <RefreshCw size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* One-tap activation banner — required to unlock alarm sound */}
        {!audioUnlocked && (
          <Card className="rounded-2xl mb-4 border-2 border-yellow-400 bg-yellow-50" data-testid="arm-pos-banner">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-yellow-900">
                <p className="font-bold flex items-center gap-2 text-sm">
                  <Bell size={16} /> {language === 'pt' ? 'Ative o som para começar o turno' : 'Activate sound to start your shift'}
                </p>
                <p className="text-xs mt-1">
                  {language === 'pt'
                    ? 'O navegador bloqueia o som até tocar uma vez. Toque no botão para garantir que o alarme toca em cada pedido.'
                    : 'The browser blocks sound until you tap once. Tap to ensure the alarm rings on every order.'}
                </p>
              </div>
              <Button
                onClick={armPos}
                className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-full animate-pulse whitespace-nowrap"
                data-testid="arm-pos-btn"
              >
                🔔 {language === 'pt' ? 'ATIVAR SOM' : 'ACTIVATE SOUND'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Settings bar */}
        <Card className="rounded-2xl mb-6">
          <CardContent className="p-4 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-[#004E64]">{language === 'pt' ? 'Restaurante' : 'Restaurant'}</Label>
              <Select value={selectedRestaurantId} onValueChange={setSelectedRestaurantId}>
                <SelectTrigger className="w-64 rounded-full" data-testid="restaurant-selector">
                  <SelectValue placeholder={language === 'pt' ? 'Escolher...' : 'Choose...'} />
                </SelectTrigger>
                <SelectContent>
                  {isAdminUser && (
                    <SelectItem value="__ALL__" data-testid="restaurant-all-option">
                      🌐 {language === 'pt' ? 'TODOS os Restaurantes' : 'ALL Restaurants'}
                    </SelectItem>
                  )}
                  {restaurants.map(r => <SelectItem key={r.restaurant_id} value={r.restaurant_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={autoPrint} onCheckedChange={setAutoPrint} data-testid="auto-print-toggle" />
              <Label className="text-sm flex items-center gap-1"><Printer size={14} /> {language === 'pt' ? 'Impressão automática' : 'Auto-print'}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} data-testid="sound-toggle" />
              <Label className="text-sm flex items-center gap-1">
                {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                {language === 'pt' ? 'Alarme sonoro' : 'Sound alarm'}
              </Label>
            </div>
            {audioUnlocked && (
              <span className="text-xs text-green-700 bg-green-100 px-3 py-1 rounded-full">
                {language === 'pt' ? '🔔 Som ativado' : '🔔 Sound on'}
              </span>
            )}
          </CardContent>
        </Card>

        {/* Active Orders */}
        <h2 className="text-lg font-bold text-[#004E64] mb-3">{language === 'pt' ? 'Pedidos Ativos' : 'Active Orders'} ({activeOrders.length})</h2>
        {activeOrders.length === 0 ? (
          <Card className="rounded-2xl"><CardContent className="p-8 text-center text-[#9FA2B4]">
            {language === 'pt' ? 'Sem pedidos ativos. À espera...' : 'No active orders. Waiting...'}
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {activeOrders.map(order => (
              <Card key={order.order_id} className="rounded-2xl border-2 border-[#FF6B35]/20" data-testid={`pos-order-${order.order_id}`}>
                <CardHeader className="pb-2">
                  {isAllMode && (
                    <Badge className="bg-[#004E64] text-white text-xs mb-2 self-start" data-testid={`pos-order-rest-${order.order_id}`}>
                      🏪 {orderRestaurantName(order)}
                    </Badge>
                  )}
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">#{order.order_id?.slice(-8)}</CardTitle>
                    <Badge className={statusColor(order.status)}>{statusLabel(order.status)}</Badge>
                  </div>
                  <p className="text-xs text-[#9FA2B4] flex items-center gap-1"><Clock size={11} />{order.created_at ? new Date(order.created_at).toLocaleTimeString() : ''}</p>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="bg-[#F4F1EA] rounded-xl p-2">
                    {(order.items || []).map((it, i) => (
                      <div key={`${order.order_id}-${it.menu_id || it.name || i}`} className="flex justify-between">
                        <span className="text-[#004E64]"><strong>{it.quantity}x</strong> {it.name}</span>
                        <span className="text-[#9FA2B4]">€{(it.price * it.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between font-bold text-[#004E64] border-t pt-2">
                    <span>{language === 'pt' ? 'Total' : 'Total'}</span>
                    <span className="text-[#FF6B35]">€{order.total?.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-[#9FA2B4] space-y-1 pt-1 border-t">
                    <p><strong>{order.customer_name}</strong></p>
                    {order.customer_phone && <p className="flex items-center gap-1"><Phone size={10} />{order.customer_phone}</p>}
                    {order.delivery_address && <p className="flex items-start gap-1"><MapPin size={10} className="mt-0.5 shrink-0" />{order.delivery_address}</p>}
                    {order.payment_method && <Badge variant="outline" className="text-xs">{order.payment_method === 'cash' ? (language === 'pt' ? 'Dinheiro' : 'Cash') : (language === 'pt' ? 'Cartão' : 'Card')}</Badge>}
                    {order.notes && <p className="bg-yellow-50 p-1 rounded text-yellow-800">📝 {order.notes}</p>}
                  </div>
                  <div className="flex gap-1 pt-2 flex-wrap">
                    {order.status === 'received' && (
                      <Button size="sm" className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-full text-xs" onClick={() => updateStatus(order.order_id, 'preparing')}>
                        <Check size={12} className="mr-1" />{language === 'pt' ? 'Aceitar' : 'Accept'}
                      </Button>
                    )}
                    {order.status === 'preparing' && (
                      <Button size="sm" className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-full text-xs" onClick={() => updateStatus(order.order_id, 'ready')}>
                        {language === 'pt' ? 'Pronto' : 'Ready'}
                      </Button>
                    )}
                    {order.status === 'ready' && (
                      <Button size="sm" className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full text-xs" onClick={() => updateStatus(order.order_id, 'out_for_delivery')}>
                        {language === 'pt' ? 'Saída' : 'Pickup'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => printOrder(order)} data-testid={`print-${order.order_id}`}>
                      <Printer size={12} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={() => { setDeleteExclude(false); setDeleteTarget(order); }}
                      data-testid={`pos-delete-${order.order_id}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                    {!['delivered', 'cancelled'].includes(order.status) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            data-testid={`cancel-order-${order.order_id}`}
                          >
                            <Ban size={12} className="mr-1" />
                            {language === 'pt' ? 'Cancelar' : 'Cancel'}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="pos-cancel-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {language === 'pt' ? 'Cancelar este pedido?' : 'Cancel this order?'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {language === 'pt'
                                ? `O cliente será notificado. ${order.payment_method === 'card' ? 'O valor pago será reembolsado automaticamente (100%).' : 'Pagamento em dinheiro — nada a reembolsar.'}`
                                : `The customer will be notified. ${order.payment_method === 'card' ? 'The paid amount will be refunded automatically (100%).' : 'Cash payment — nothing to refund.'}`}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="pos-cancel-dismiss">
                              {language === 'pt' ? 'Voltar' : 'Go back'}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => updateStatus(order.order_id, 'cancelled')}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid="pos-cancel-confirm"
                            >
                              {language === 'pt' ? 'Sim, cancelar' : 'Yes, cancel'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Cancelled orders — kept visible with a clear indication */}
        {cancelledOrders.length > 0 && (
          <div className="mb-8" data-testid="pos-cancelled-section">
            <h2 className="text-lg font-bold text-red-600 mb-3 flex items-center gap-2">
              <Ban size={18} />
              {language === 'pt' ? 'Pedidos Cancelados' : 'Cancelled Orders'} ({cancelledOrders.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cancelledOrders.slice(0, 12).map(order => (
                <Card key={order.order_id} className="rounded-2xl border-2 border-red-200 bg-red-50/60 relative overflow-hidden" data-testid={`pos-cancelled-${order.order_id}`}>
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-red-600 text-white">{language === 'pt' ? 'CANCELADO' : 'CANCELLED'}</Badge>
                  </div>
                  <CardContent className="p-4 space-y-1">
                    {isAllMode && (
                      <p className="text-[10px] text-[#004E64] font-semibold">🏪 {orderRestaurantName(order)}</p>
                    )}
                    <p className="font-bold text-[#004E64] text-sm">#{order.order_id?.slice(-8)}</p>
                    <p className="text-xs text-[#9FA2B4]">{order.customer_name} • €{order.total?.toFixed(2)}</p>
                    {order.refund_amount > 0 && (
                      <p className="text-xs text-green-700 font-semibold">
                        {language === 'pt' ? `Reembolsado: €${order.refund_amount.toFixed(2)}` : `Refunded: €${order.refund_amount.toFixed(2)}`}
                      </p>
                    )}
                    <div className="flex gap-1 mt-1">
                      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => printOrder(order)}>
                        <Printer size={12} className="mr-1" />{language === 'pt' ? 'Imprimir' : 'Print'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={() => { setDeleteExclude(false); setDeleteTarget(order); }}
                        data-testid={`pos-delete-${order.order_id}`}
                      >
                        <Trash2 size={12} className="mr-1" />{language === 'pt' ? 'Eliminar' : 'Delete'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Closed orders */}
        {closedOrders.length > 0 && (
          <details className="mt-8">
            <summary className="text-sm font-semibold text-[#9FA2B4] cursor-pointer">
              {language === 'pt' ? 'Histórico recente' : 'Recent history'} ({closedOrders.length})
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              {closedOrders.slice(0, 12).map(order => (
                <div key={order.order_id} className="bg-white rounded-xl p-3 border border-[#004E64]/10">
                  {isAllMode && (
                    <p className="text-[10px] text-[#004E64] font-semibold mb-1">🏪 {orderRestaurantName(order)}</p>
                  )}
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-semibold">#{order.order_id?.slice(-8)}</span>
                    <Badge className={statusColor(order.status)}>{statusLabel(order.status)}</Badge>
                  </div>
                  <p className="text-xs text-[#9FA2B4]">{order.customer_name} • €{order.total?.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Setup help */}
        <Card className="rounded-2xl mt-8 bg-[#F4F1EA] border-0">
          <CardContent className="p-4 text-xs text-[#004E64]/80">
            <p className="font-bold mb-1 text-[#004E64]">⚙️ {language === 'pt' ? 'Configurar impressão automática (kiosk)' : 'Set up auto-printing (kiosk)'}</p>
            <p className="mb-1">
              {language === 'pt' ? 'Para imprimir sem caixa de diálogo, abra o Chrome neste POS com:' : 'To print silently, launch Chrome on this POS with:'}
            </p>
            <code className="block bg-white p-2 rounded text-[10px] text-[#004E64] overflow-x-auto">
              chrome --kiosk-printing --kiosk {window.location.origin}/pos
            </code>
            <p className="mt-2">
              {language === 'pt'
                ? 'Defina a impressora térmica MTM como impressora padrão do sistema operativo. Tamanho do papel: 80mm.'
                : 'Set the MTM thermal printer as the OS default printer. Paper size: 80mm.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RestaurantPOSDashboard;
